import type { WebApp } from "@twa-dev/types";

/**
 * Thin, typed wrapper around the official Telegram Web App runtime
 * (window.Telegram.WebApp, loaded via telegram-web-app.js in index.html).
 *
 * We deliberately use the raw WebApp API rather than a higher-level SDK: it is
 * the most stable surface and maps 1:1 to Telegram's docs. It can be swapped for
 * @telegram-apps/sdk-react later without touching feature code, since UI reads
 * everything through these helpers.
 */

export function getWebApp(): WebApp | null {
  if (typeof window === "undefined") return null;
  return window.Telegram?.WebApp ?? null;
}

/**
 * The raw initData query string to authenticate with the backend.
 *
 * Primary source is the official SDK (window.Telegram.WebApp.initData). But that
 * script is loaded cross-origin from telegram.org and can fail to execute inside
 * a Telegram in-app webview (weak network, CDN hiccup). Telegram ALSO passes the
 * launch parameters directly in the page URL hash as `tgWebAppData`, so we fall
 * back to parsing that — making auth independent of the external script.
 */
export function getInitData(): string {
  const fromSdk = getWebApp()?.initData;
  if (fromSdk && fromSdk.length > 0) return fromSdk;
  return getInitDataFromHash();
}

/** Parse `tgWebAppData` out of the launch URL hash (Telegram's raw initData). */
export function getInitDataFromHash(): string {
  if (typeof window === "undefined") return "";
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash) return "";
  const data = new URLSearchParams(hash).get("tgWebAppData");
  return data ?? "";
}

/** True when running inside a real Telegram client (initData present). */
export function isTelegramEnv(): boolean {
  return getInitData().length > 0;
}

/**
 * Prepares the Mini App on load: mark ready, expand to full height, and mirror
 * Telegram's theme onto CSS variables so the UI matches the user's client theme.
 * Safe to call outside Telegram (no-op with sensible fallbacks).
 */
export function initTelegram(): WebApp | null {
  const wa = getWebApp();
  if (!wa) {
    applyThemeFallback();
    return null;
  }

  wa.ready();
  wa.expand();
  applyTheme(wa);

  // Keep CSS variables in sync if the user switches theme mid-session.
  wa.onEvent("themeChanged", () => applyTheme(wa));

  return wa;
}

function applyTheme(wa: WebApp): void {
  const root = document.documentElement;
  const tp = wa.themeParams;
  const set = (name: string, value: string | undefined, fallback: string) =>
    root.style.setProperty(name, value ?? fallback);

  set("--tg-bg", tp.bg_color, "#ffffff");
  set("--tg-text", tp.text_color, "#000000");
  set("--tg-hint", tp.hint_color, "#707579");
  set("--tg-link", tp.link_color, "#2481cc");
  set("--tg-button", tp.button_color, "#2481cc");
  set("--tg-button-text", tp.button_text_color, "#ffffff");
  set("--tg-secondary-bg", tp.secondary_bg_color, "#f4f4f5");

  root.dataset.colorScheme = wa.colorScheme ?? "light";
}

function applyThemeFallback(): void {
  const root = document.documentElement;
  const prefersDark =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)").matches;
  root.dataset.colorScheme = prefersDark ? "dark" : "light";
}

/**
 * Wire the native MainButton (bottom bar). Returns a cleanup function that
 * hides the button and detaches the handler. No-op outside Telegram.
 */
export function showMainButton(text: string, onClick: () => void): () => void {
  const wa = getWebApp();
  if (!wa) return () => {};

  const mb = wa.MainButton;
  mb.setText(text);
  mb.onClick(onClick);
  mb.show();
  mb.enable();

  return () => {
    mb.offClick(onClick);
    mb.hide();
  };
}

/**
 * Native haptic feedback. A cheap, high-value "feels expensive" touch on key
 * actions (add to cart, spin, order placed). No-op outside Telegram or on
 * clients that don't support it.
 */
export function haptic(
  kind: "light" | "medium" | "heavy" | "success" | "warning" | "error",
): void {
  const hf = getWebApp()?.HapticFeedback;
  if (!hf) return;
  try {
    if (kind === "success" || kind === "warning" || kind === "error") {
      hf.notificationOccurred(kind);
    } else {
      hf.impactOccurred(kind);
    }
  } catch {
    /* unsupported client — ignore */
  }
}

/** Open an external URL from within Telegram (falls back to window.open). */
export function openLink(url: string): void {
  const wa = getWebApp();
  if (wa) wa.openLink(url);
  else window.open(url, "_blank", "noopener");
}

/** Wire the native BackButton. Returns a cleanup function. No-op outside Telegram. */
export function showBackButton(onClick: () => void): () => void {
  const wa = getWebApp();
  if (!wa) return () => {};

  const bb = wa.BackButton;
  bb.onClick(onClick);
  bb.show();

  return () => {
    bb.offClick(onClick);
    bb.hide();
  };
}
